/**
 * The service worker's `external` tier — the policy half (project:
 * alert-lives-service-worker-freezes-external-stac-catalogs-at-first-load,
 * 2026-09-30).
 *
 * Where `cacheExternal` is on and no `alwaysFetch` entry answers first
 * (alert.live since 2026-08-26), the worker's `cache` plugin serves every
 * cross-origin GET from Cache Storage. Before this module it did so
 * cache-first and forever: no header was read, the request's `cache:` mode
 * was ignored, and the bucket was never cleared, so an external STAC
 * catalog added by URL was frozen at its first load. Now:
 *
 *   - A request that asks to bypass caches (`cache: 'no-store'`, `'reload'`
 *     or `'no-cache'`) is not answered from any tier (`skipsLookup`); the
 *     network fetch that follows carries the same mode into the HTTP cache.
 *   - A hit is served at once and refreshed in the background
 *     (stale-while-revalidate, `createBackgroundRefresher`), so a change
 *     upstream shows on the next load. The refresh uses the request's own
 *     cache mode, normally `default`, so the browser's HTTP cache and the
 *     upstream's `Cache-Control` decide whether it touches the network: a
 *     tile with a long max-age costs nothing, a `no-cache` catalog costs a
 *     conditional request.
 *   - Nothing is stored that the request or the response forbids
 *     (`mayStore`): `cache: 'no-store'` on the request, or `no-store` in
 *     the response's `Cache-Control`.
 *   - A refresh that finds the file gone (404 or 410) or marked `no-store`
 *     deletes the stored copy. A server error or a network failure keeps
 *     it, which is what makes the tier useful offline.
 *
 * The bucket was renamed (`external` → `external-v2`) so the fixed worker's
 * first activate drops every copy frozen under the old rules
 * (`LEGACY_EXTERNAL_CACHENAMES`). Imports nothing; unit-tested in
 * test/sw-external-cache.test.mjs. The lasting replacement is the
 * header-driven plugin of the named-caches project.
 */

export const EXTERNAL_CACHENAME = 'external-v2'

/** Buckets from before this policy; deleted on activate. */
export const LEGACY_EXTERNAL_CACHENAMES = ['external']

const BYPASS_MODES = new Set(['no-store', 'reload', 'no-cache'])

/** The request asked not to be answered from a cache. */
export function skipsLookup(request) {
    return BYPASS_MODES.has(request?.cache)
}

/** The response's own headers forbid storing it. */
export function responseForbidsStore(response) {
    const cc = response?.headers?.get?.('Cache-Control') || ''
    return /(^|[\s,])no-store([\s,;=]|$)/i.test(cc)
}

/** Whether a network response to `request` may be put in a worker cache. */
export function mayStore(request, response) {
    if (!request || !response) return false
    if (request.method !== 'GET') return false
    if (request.cache === 'no-store') return false
    if (!response.ok) return false
    return !responseForbidsStore(response)
}

const GONE = new Set([404, 410])

/**
 * Background refresh of cache hits, one in flight per URL.
 *
 * @param {object} deps
 * @param {(req: Request) => Promise<Response>} deps.fetch
 * @param {(...a) => void} [deps.log]
 * @returns {{ refresh(request: Request, cache: Cache, matchOptions?: object): Promise<string>, inFlight(): number }}
 *   `refresh` resolves to what it did: 'stored', 'deleted', 'kept', or
 *   'joined' (another refresh of that URL was already running).
 */
export function createBackgroundRefresher({ fetch, log = () => {} }) {
    const running = new Map()

    async function run(request, cache, matchOptions) {
        let res
        try {
            res = await fetch(request)
        } catch (err) {
            log('external refresh failed, keeping the stored copy:', request.url, err?.message || err)
            return 'kept'
        }
        if (mayStore(request, res)) {
            await cache.put(request, res)
            return 'stored'
        }
        if (GONE.has(res.status) || (res.ok && responseForbidsStore(res))) {
            await cache.delete(request, matchOptions)
            log('external refresh dropped the stored copy:', request.url, res.status)
            return 'deleted'
        }
        return 'kept'
    }

    return {
        refresh(request, cache, matchOptions = {}) {
            const key = request.url
            if (running.has(key)) return running.get(key).then(() => 'joined')
            const p = run(request, cache, matchOptions).finally(() => running.delete(key))
            running.set(key, p)
            return p
        },
        inFlight() {
            return running.size
        },
    }
}
